import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useEmergencia } from '../../lib/emergencia';
import { Modal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { UpperInput } from '../../components/inputs/UpperInput';
import { PhoneInput } from '../../components/inputs/PhoneInput';
import { WhatsAppPickerInput } from '../../components/inputs/WhatsAppPickerInput';
import { BuscarCnpjButton, type CnpjDados } from '../../components/inputs/BuscarCnpjButton';
import { MenuAcoesLinha, type AcaoLinha } from '../../components/MenuAcoesLinha';
import { StatusBadge } from '../../components/StatusBadge';
import { Pencil, Ban, Receipt, User, Phone, MapPin, Star, Trash2, Plus } from 'lucide-react';
import { SecaoForm } from '../../components/SecaoForm';
import { ComboBox } from '../../components/ComboBox';
import { ModalNovaCidade, garantirCidade, type Cidade } from '../../components/cidades/ModalNovaCidade';
import { Avatar } from '../../components/Avatar';
import { formatCpfCnpj, formatTelefoneBR, classificarIE, LOGIN_DONO, nomeCadastroCliente } from '@fiodecobre/shared';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { useAuth } from '../../lib/auth';

interface Cliente {
  id: number;
  codigo_legado: string | null;
  tipo_pessoa: 'PF' | 'PJ';
  nome: string;
  razao_social: string | null;
  cpf_cnpj: string | null;
  telefone: string | null;
  whatsapp: string | null;
  email: string | null;
  cidade: string | null;
  uf: string | null;
  status: string;
}

interface TelefoneCliente { numero: string; principal: boolean }

// Enriquecimento de cada numero de WhatsApp com o contato do Atendimento (foto + nome do
// perfil do WhatsApp + janela 24h). Vem pronto do GET /:id/destinatarios-whatsapp.
interface DestinatarioWA {
  numero: string;
  nome: string | null;
  foto_url: string | null;
  janela_aberta: boolean;
}
// Chave estavel entre o numero do form (digitos) e o numero normalizado do backend:
// os 8 digitos finais (assinante), invariantes a codigo de pais / 9o digito.
function chaveWA(numero: string): string {
  return (numero || '').replace(/\D/g, '').slice(-8);
}

const formVazio = {
  codigo_legado: '', tipo_pessoa: 'PF' as 'PF' | 'PJ', nome: '', razao_social: '',
  cpf_cnpj: '', inscricao_estadual: '', inscricao_municipal: '', telefone: '', whatsapp: '', email: '',
  cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '',
  codigo_ibge_cidade: '',
  consignacao: false,
  programa_prestador: false,
  travar_nota_a4: false,
  telefones: [] as TelefoneCliente[],
};

interface ViaCepResp {
  cep?: string;
  logradouro?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  ibge?: string;
  erro?: boolean;
}

async function consultarViaCep(cep: string): Promise<ViaCepResp | null> {
  const limpo = cep.replace(/\D/g, '');
  if (limpo.length !== 8) return null;
  try {
    const resp = await fetch(`https://viacep.com.br/ws/${limpo}/json/`);
    if (!resp.ok) return null;
    const data = (await resp.json()) as ViaCepResp;
    if (data.erro) return null;
    return data;
  } catch {
    return null;
  }
}

// Tira acento mantendo a letra (JOÃO -> JOAO). Nome/razao social ficam sem acento
// pra facilitar a busca — NAO e exigencia fiscal.
function semAcento(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

// Apara espaco no inicio/fim e colapsa espacos duplos. Espaco sobrando no endereco
// derruba a NF-e (cStat 225 na SEFAZ-GO) — sanitiza todo campo de texto ao salvar.
function limparTexto(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

// Visual de campo travado pelo CEP. Inputs tem --input-bg com !important global,
// entao a cor de fundo so muda sobrescrevendo a propria variavel (ver CLAUDE.md).
const travadoStyle = { '--input-bg': 'var(--input-bg-disabled, #eef0f2)', cursor: 'not-allowed' } as CSSProperties;

export function Clientes() {
  const toast = useToast();
  const dialog = useDialog();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { usuario } = useAuth();
  const emergencia = useEmergencia();
  const ehAdmin = usuario?.perfil === 'admin';
  // Selo do Programa do Prestador (DA-435): so o dono marca. Aqui e' so a decisao de MOSTRAR —
  // quem recusa de verdade e' o backend (`conferirSeloPrograma` em clientes.routes.ts).
  const ehDono = (usuario?.login ?? '').trim().toUpperCase() === LOGIN_DONO;
  const [itens, setItens] = useState<Cliente[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [editando, setEditando] = useState<Cliente | null>(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(formVazio);
  // Foto/nome/janela do WhatsApp de cada numero, indexado por chaveWA(numero).
  const [waInfo, setWaInfo] = useState<Map<string, DestinatarioWA>>(new Map());
  const [confirmar, setConfirmar] = useState<Cliente | null>(null);
  // CEP no comando do endereco: trava rua/bairro/cidade/UF (Numero/Complemento ficam livres).
  const [cepAtivo, setCepAtivo] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [confirmarRemoverCep, setConfirmarRemoverCep] = useState(false);
  // avisa 1x por abertura do modal que nome vai sem acento (pra nao achar que bugou)
  const [avisouAcento, setAvisouAcento] = useState(false);
  // DA-878: o campo Cidade escolhe do cadastro de Cidades (nome + UF + codigo IBGE juntos).
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [novaCidade, setNovaCidade] = useState(false);
  useEffect(() => {
    if (!modal) return;
    api.get<Cidade[]>('/api/cidades').then(setCidades).catch(() => { /* sem lista: o combo mostra so a atual */ });
  }, [modal]);

  function aplicarCidade(c: Cidade) {
    setCidades((prev) => (prev.some((x) => x.codigo_ibge === c.codigo_ibge)
      ? prev : [...prev, c].sort((a, b) => a.nome.localeCompare(b.nome))));
    setForm((f) => ({ ...f, cidade: c.nome, uf: c.uf, codigo_ibge_cidade: c.codigo_ibge }));
  }

  // CEP/SEFAZ trouxeram o codigo: garante a cidade no cadastro e usa a grafia dele. Se o cadastro
  // falhar (IBGE fora), fica com o que veio — o servidor completa nome/UF ao salvar se ela existir.
  async function garantirEAplicar(codigoIbge: string | null | undefined) {
    if (!codigoIbge || !/^\d{7}$/.test(codigoIbge)) return;
    try {
      aplicarCidade(await garantirCidade(codigoIbge));
    } catch {
      toast.aviso('Nao consegui conferir a cidade no cadastro agora — confira o campo Cidade.');
    }
  }

  // nome/razao social: tira acento na hora e avisa na 1a vez
  function definirNomeSemAcento(campo: 'nome' | 'razao_social', v: string) {
    const limpo = semAcento(v);
    if (limpo !== v && !avisouAcento) {
      toast.aviso('Os nomes ficam em maiúsculo, sem acento e sem Ç.');
      setAvisouAcento(true);
    }
    setForm((f) => ({ ...f, [campo]: limpo }));
  }

  async function carregar() {
    setCarregando(true);
    try {
      const lista = await api.get<Cliente[]>('/api/clientes', {
        query: { busca: busca || undefined, incluir_inativos: mostrarInativos },
      });
      setItens(lista);
    } catch (e: any) {
      toast.erro(e.message);
    } finally { setCarregando(false); }
  }
  useEffect(() => {
    const t = setTimeout(carregar, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, mostrarInativos]);

  // Deep-link ?abrir=<id>: abre o cadastro do cliente direto (ex: vindo da troca de
  // modelo de nota em /notas-fiscais, quando o cadastro esta incompleto pra NF-e).
  // ?novo=1 reabre o "Novo cliente" (vazio — o digitado nao sobrevive).
  useEffect(() => {
    const abrirId = searchParams.get('abrir');
    if (abrirId) void abrirEditarPorId(parseInt(abrirId, 10));
    else if (searchParams.get('novo')) abrirNovo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O modal aberto mora na URL pra sobreviver ao F5 (pedido dele, 03/11/2026): o efeito acima
  // le de volta. Na montagem o modal ainda esta fechado e limpa o parametro; a abertura o repoe.
  useEffect(() => {
    setSearchParams((p) => {
      const n = new URLSearchParams(p);
      n.delete('abrir');
      n.delete('novo');
      if (modal && editando) n.set('abrir', String(editando.id));
      else if (modal) n.set('novo', '1');
      return n.toString() === p.toString() ? p : n;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal, editando?.id]);

  function abrirNovo() {
    setEditando(null);
    setForm({ ...formVazio, telefones: [] });
    setWaInfo(new Map());
    setCepAtivo(false);
    setAvisouAcento(false);
    setModal(true);
  }
  async function abrirEditar(c: Cliente) {
    await abrirEditarPorId(c.id);
  }
  async function abrirEditarPorId(id: number) {
    try {
      const detalhe = await api.get<Cliente & typeof formVazio & { telefones?: TelefoneCliente[] }>(`/api/clientes/${id}`);
      setEditando({
        id: detalhe.id,
        codigo_legado: detalhe.codigo_legado ?? null,
        tipo_pessoa: detalhe.tipo_pessoa,
        nome: detalhe.nome,
        razao_social: detalhe.razao_social ?? null,
        cpf_cnpj: detalhe.cpf_cnpj ?? null,
        telefone: detalhe.telefone ?? null,
        whatsapp: detalhe.whatsapp ?? null,
        email: detalhe.email ?? null,
        cidade: detalhe.cidade ?? null,
        uf: detalhe.uf ?? null,
        status: detalhe.status ?? 'ativo',
      });
      setForm({
        codigo_legado: detalhe.codigo_legado ?? '',
        tipo_pessoa: detalhe.tipo_pessoa,
        nome: detalhe.nome,
        razao_social: detalhe.razao_social ?? '',
        cpf_cnpj: detalhe.cpf_cnpj ?? '',
        inscricao_estadual: detalhe.inscricao_estadual ?? '',
        inscricao_municipal: detalhe.inscricao_municipal ?? '',
        telefone: detalhe.telefone ?? '',
        whatsapp: detalhe.whatsapp ?? '',
        email: detalhe.email ?? '',
        cep: detalhe.cep ?? '',
        endereco: detalhe.endereco ?? '',
        numero: detalhe.numero ?? '',
        complemento: detalhe.complemento ?? '',
        bairro: detalhe.bairro ?? '',
        cidade: detalhe.cidade ?? '',
        uf: detalhe.uf ?? '',
        codigo_ibge_cidade: detalhe.codigo_ibge_cidade ?? '',
        consignacao: detalhe.consignacao ?? false,
        programa_prestador: detalhe.programa_prestador ?? false,
        travar_nota_a4: detalhe.travar_nota_a4 ?? false,
        telefones: (detalhe.telefones && detalhe.telefones.length)
          ? detalhe.telefones.map((t) => ({ numero: (t.numero ?? '').replace(/\D/g, ''), principal: !!t.principal }))
          : (detalhe.whatsapp ? [{ numero: detalhe.whatsapp.replace(/\D/g, ''), principal: true }] : []),
      });
      // cliente que ja tem CEP: endereco fica travado (use "Remover CEP" pra editar a mao)
      setCepAtivo(!!(detalhe.cep && detalhe.cep.replace(/\D/g, '').length === 8));
      setAvisouAcento(false);
      setModal(true);
      // Enriquece os numeros com foto/nome/janela do WhatsApp (nao bloqueia a abertura do modal).
      setWaInfo(new Map());
      carregarWaInfo(id);
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  // Busca foto + nome do WhatsApp + janela 24h de cada numero do cliente (contatos do
  // Atendimento). Falha silenciosa: sem enriquecimento a lista mostra so os numeros.
  async function carregarWaInfo(id: number) {
    try {
      const resp = await api.get<{ destinatarios: DestinatarioWA[] }>(`/api/clientes/${id}/destinatarios-whatsapp`);
      const mapa = new Map<string, DestinatarioWA>();
      for (const d of resp.destinatarios || []) mapa.set(chaveWA(d.numero), d);
      setWaInfo(mapa);
    } catch {
      // sem rede/permissao: segue sem foto
    }
  }

  // Botao "Buscar endereco": sobrescreve rua/bairro/cidade/UF pelo ViaCEP e trava os campos.
  // Substitui o auto-preenchimento silencioso de antes (so preenchia vazio, sem avisar) —
  // foi por ele que digitaram o endereco a mao e entrou espaco sobrando que derrubou a NF-e.
  async function buscarEndereco() {
    if (form.cep.replace(/\D/g, '').length !== 8) {
      toast.aviso('Digite um CEP com 8 digitos.');
      return;
    }
    setBuscandoCep(true);
    try {
      const data = await consultarViaCep(form.cep);
      if (!data) {
        toast.erro('CEP nao encontrado.');
        return;
      }
      setForm((f) => ({
        ...f,
        endereco: (data.logradouro ?? '').toUpperCase(),
        bairro: (data.bairro ?? '').toUpperCase(),
        cidade: (data.localidade ?? '').toUpperCase(),
        uf: (data.uf ?? '').toUpperCase(),
        codigo_ibge_cidade: data.ibge ?? '',
      }));
      await garantirEAplicar(data.ibge);
      setCepAtivo(true);
      toast.sucesso('Endereco preenchido pelo CEP.');
    } finally {
      setBuscandoCep(false);
    }
  }

  // "Remover CEP": limpa CEP + endereco preenchido e libera tudo pra digitar manual.
  function removerCep() {
    setForm((f) => ({ ...f, cep: '', endereco: '', bairro: '', cidade: '', uf: '', codigo_ibge_cidade: '' }));
    setCepAtivo(false);
    setConfirmarRemoverCep(false);
  }

  // --- Lista de numeros de WhatsApp do cliente ---
  function adicionarNumero(numero: string) {
    const num = (numero ?? '').replace(/\D/g, '');
    if (!num) return;
    setForm((f) => f.telefones.some((t) => t.numero === num)
      ? f
      : { ...f, telefones: [...f.telefones, { numero: num, principal: f.telefones.length === 0 }] });
  }
  function removerNumero(idx: number) {
    setForm((f) => {
      const rest = f.telefones.filter((_, i) => i !== idx);
      if (rest.length && !rest.some((t) => t.principal)) rest[0]!.principal = true;
      return { ...f, telefones: rest };
    });
  }
  function definirPrincipal(idx: number) {
    setForm((f) => ({ ...f, telefones: f.telefones.map((t, i) => ({ ...t, principal: i === idx })) }));
  }

  async function salvar() {
    try {
      // Limpeza final: nenhum campo de texto sai com espaco no inicio/fim nem duplo
      // (espaco sobrando derruba a NF-e); nome/razao social tambem saem sem acento.
      const limpo = {
        ...form,
        // Mesma peca do servidor: maiusculo, sem acento e sem Ç.
        nome: nomeCadastroCliente(form.nome),
        razao_social: nomeCadastroCliente(form.razao_social),
        endereco: limparTexto(form.endereco),
        numero: limparTexto(form.numero),
        complemento: limparTexto(form.complemento),
        bairro: limparTexto(form.bairro),
        cidade: limparTexto(form.cidade),
        uf: limparTexto(form.uf),
        email: limparTexto(form.email),
        inscricao_estadual: limparTexto(form.inscricao_estadual),
        inscricao_municipal: limparTexto(form.inscricao_municipal),
      };
      // WhatsApp principal espelha pra coluna unica (NF/orcamento/caixa/Pix leem ela).
      const principal = form.telefones.find((t) => t.principal)?.numero ?? form.telefones[0]?.numero ?? '';
      const payload = { ...limpo, whatsapp: principal, telefones: form.telefones, email: limpo.email || null, codigo_legado: limpo.codigo_legado || null };
      if (editando) {
        await api.put(`/api/clientes/${editando.id}`, payload);
        toast.sucesso('atualizado');
      } else {
        await api.post('/api/clientes', payload);
        toast.sucesso('criado');
      }
      setModal(false);
      void carregar();
    } catch (e: any) {
      // WhatsApp/telefone ja pertence a outro cliente — oferece abrir o cadastro existente.
      const corpo = e instanceof ApiError ? e.corpo : null;
      if (e?.status === 409 && corpo?.erro === 'whatsapp_duplicado' && corpo.cliente_existente) {
        const abrir = await dialog.confirmar(
          `${corpo.mensagem ?? 'Este WhatsApp ja esta cadastrado em outro cliente.'} Deseja abrir o cadastro desse cliente?`,
          { titulo: 'WhatsApp ja cadastrado', textoConfirmar: 'Abrir cadastro existente' },
        );
        if (abrir) {
          setModal(false);
          await abrirEditarPorId(corpo.cliente_existente.id);
        }
        return;
      }
      toast.erro(e.message);
    }
  }

  async function remover() {
    if (!confirmar) return;
    try {
      await api.delete(`/api/clientes/${confirmar.id}`);
      toast.sucesso('inativado');
      setConfirmar(null);
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  const sticky = useStickyHeader();

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">Clientes</h2>
          <div className="pagina-acoes">
            <input type="search" placeholder="Buscar nome, codigo, CPF..." className="busca" style={{ minWidth: 720 }} value={busca} onChange={(e) => setBusca(semAcento(e.target.value))} />
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
              Mostrar inativos
            </label>
            <button type="button" className="btn-primario" onClick={abrirNovo}>+ Novo</button>
          </div>
        </div>
      </div>

      {carregando ? <p className="muted">Carregando...</p> : itens.length === 0 ? <p className="muted">Nenhum cliente.</p> : (
        <table className="tabela" style={sticky.tableStyle}>
          <thead><tr>
            <th style={sticky.thStyle}>Codigo</th>
            <th style={sticky.thStyle}>Nome fantasia</th>
            <th style={sticky.thStyle}>Razao social</th>
            <th style={sticky.thStyle}>Tipo</th>
            <th style={sticky.thStyle}>CPF/CNPJ</th>
            <th style={sticky.thStyle}>Telefone</th>
            <th style={sticky.thStyle}>Cidade</th>
            <th style={sticky.thStyle}>Status</th>
            <th style={{ ...sticky.thStyle, textAlign: 'right' }}>Acoes</th>
          </tr></thead>
          <tbody>
            {itens.map((c) => {
              const acoes: AcaoLinha[] = [
                { label: 'Extrato financeiro', icon: Receipt, onClick: () => navigate(`/clientes/${c.id}/extrato`) },
                { label: 'Editar', icon: Pencil, onClick: () => void abrirEditar(c) },
                { label: 'Inativar', icon: Ban, perigoso: true, oculto: !(c.status === 'ativo' && ehAdmin) || emergencia, onClick: () => setConfirmar(c) },
              ];
              return (
                <tr key={c.id} onDoubleClick={() => abrirEditar(c)}>
                  <td><span className="codigo-curto">{c.codigo_legado ?? '—'}</span></td>
                  <td>
                    <div className="cel-desc-titulo">{c.nome}</div>
                  </td>
                  {/* Razao social em coluna propria (era subtitulo do nome) — PF nao tem. */}
                  <td>{c.razao_social || '—'}</td>
                  <td>{c.tipo_pessoa}</td>
                  <td>{formatCpfCnpj(c.cpf_cnpj)}</td>
                  {/* `||`, nao `??`: ficha salva pelo modal grava o Telefone vazio como '' (nao null),
                      e o `??` parava nele — o WhatsApp sumia da lista (ADELVAN, 30/10/2026). */}
                  <td>{c.telefone || c.whatsapp || '—'}</td>
                  <td>{c.cidade ? `${c.cidade}/${c.uf ?? ''}` : '—'}</td>
                  <td><StatusBadge ativo={c.status === 'ativo'} /></td>
                  <td style={{ textAlign: 'right' }}>
                    <MenuAcoesLinha acoes={acoes} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Modal aberto={modal} onFechar={() => setModal(false)} titulo={editando ? 'Editar cliente' : 'Novo cliente'} largura={1235}
        rodape={
          <>
            {editando && (
              <button
                type="button"
                className="btn-secundario"
                onClick={() => { setModal(false); navigate(`/clientes/${editando.id}/extrato`); }}
                title="Ver historico financeiro do cliente"
                style={{ marginRight: 'auto' }}
              >
                Extrato
              </button>
            )}
            {cepAtivo && (
              <button
                type="button"
                className="btn-secundario"
                onClick={() => setConfirmarRemoverCep(true)}
                title="Limpar o CEP e liberar o endereço pra digitar a mão"
              >
                Remover CEP
              </button>
            )}
            <button type="button" className="btn-secundario" onClick={() => setModal(false)}>Cancelar</button>
            {/* MODO EMERGENCIA: cliente NOVO grava no aparelho e sobe depois; alterar um existente precisa do servidor. */}
            {emergencia && editando && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>Alterar cliente existente fica indisponivel no modo emergencia</span>
            )}
            <button type="button" className="btn-primario" onClick={salvar} disabled={!form.nome || (emergencia && !!editando)}>Salvar</button>
          </>
        }
      >
        {/* Respiro vertical 6 + A4/obra lado a lado: cabe sem rolar em janela de 730px
            (zoom 125%) mesmo com o selo do Parceiro — medido 03/11/2026. */}
        <div className="form-grid form-grid-12" style={{ columnGap: 10, rowGap: 6 }}>
          <SecaoForm icone={User} titulo="Identificação" primeira />
          <label style={{ gridColumn: 'span 2' }}>
            Codigo
            <input
              value={form.codigo_legado}
              readOnly
              placeholder="(automatico)"
              title="Codigo atribuido automaticamente pelo sistema ao salvar"
              style={{ background: 'var(--input-bg-disabled, #f3f4f6)', color: 'var(--text-muted)', cursor: 'not-allowed' }}
            />
          </label>
          <label style={{ gridColumn: 'span 2' }}>
            Tipo
            <select value={form.tipo_pessoa} onChange={(e) => setForm({ ...form, tipo_pessoa: e.target.value as 'PF' | 'PJ' })}>
              <option value="PF">Pessoa fisica</option>
              <option value="PJ">Pessoa juridica</option>
            </select>
          </label>
          <label style={{ gridColumn: form.tipo_pessoa === 'PJ' ? 'span 4' : 'span 8' }}>
            Nome / Nome fantasia
            <UpperInput value={form.nome} onChange={(v) => definirNomeSemAcento('nome', v)} autoFocus />
          </label>
          {form.tipo_pessoa === 'PJ' && (
            <label style={{ gridColumn: 'span 4' }}>
              Razao social
              <UpperInput value={form.razao_social} onChange={(v) => definirNomeSemAcento('razao_social', v)} />
            </label>
          )}
          <label style={{ gridColumn: 'span 6' }}>
            CPF/CNPJ
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <input
                value={form.cpf_cnpj}
                onChange={(e) => setForm({ ...form, cpf_cnpj: e.target.value })}
                style={{ flex: 1 }}
              />
              {form.tipo_pessoa === 'PJ' && (
                <BuscarCnpjButton
                  cnpj={form.cpf_cnpj}
                  onResult={async (d: CnpjDados) => {
                    setForm((f) => ({
                      ...f,
                      nome: d.nome_fantasia || d.razao_social || f.nome,
                      razao_social: d.razao_social || f.razao_social,
                      inscricao_estadual: d.inscricao_estadual || f.inscricao_estadual,
                      codigo_ibge_cidade: d.codigo_ibge_cidade || f.codigo_ibge_cidade,
                      email: d.email || f.email,
                      telefone: d.telefone || f.telefone,
                      whatsapp: d.whatsapp || f.whatsapp,
                      cep: d.cep || f.cep,
                      endereco: d.endereco || f.endereco,
                      numero: d.numero || f.numero || 'SN',
                      complemento: d.complemento || f.complemento,
                      bairro: d.bairro || f.bairro,
                      cidade: d.cidade || f.cidade,
                      uf: d.uf || f.uf,
                    }));
                    if (d.cep) {
                      const via = await consultarViaCep(d.cep);
                      if (via) {
                        setForm((f) => ({
                          ...f,
                          // completa rua/bairro pelo CEP so quando a Receita nao trouxe (nao sobrescreve)
                          endereco: f.endereco || (via.logradouro ?? '').toUpperCase(),
                          bairro: f.bairro || (via.bairro ?? '').toUpperCase(),
                          cidade: (via.localidade ?? f.cidade).toUpperCase(),
                          uf: (via.uf ?? f.uf).toUpperCase(),
                          codigo_ibge_cidade: via.ibge || f.codigo_ibge_cidade,
                        }));
                      }
                      // endereco veio de fonte oficial (Receita/CEP) -> trava (Remover CEP libera)
                      setCepAtivo(true);
                      await garantirEAplicar(via?.ibge || d.codigo_ibge_cidade);
                    } else {
                      await garantirEAplicar(d.codigo_ibge_cidade);
                    }
                  }}
                />
              )}
            </div>
          </label>
          <label style={{ gridColumn: 'span 3' }}>
            Inscricao estadual
            <input value={form.inscricao_estadual} onChange={(e) => setForm({ ...form, inscricao_estadual: e.target.value })} />
            {form.inscricao_estadual.trim() && !classificarIE(form.inscricao_estadual).reconhecido && (
              <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                Use apenas numeros, ou "ISENTO". Texto invalido faz a NF-e sair como nao contribuinte.
              </span>
            )}
          </label>
          <label style={{ gridColumn: 'span 3' }}>
            Inscricao municipal
            <input
              value={form.inscricao_municipal}
              maxLength={15}
              onChange={(e) => setForm({ ...form, inscricao_municipal: e.target.value })}
            />
          </label>
          {/* Trava do modelo da nota (migration 0263, 01/09/2026, pedido dele com o print marcando
              esta linha). Empresa que so aceita NF-e A4: o envio ao caixa, a finalizacao, o
              fechamento da Venda CM e a emissao manual recusam Consumidor. O rotulo diz a
              CONSEQUENCIA, nao o campo — quem marca precisa saber o que trava. */}
          <label style={{ gridColumn: 'span 6', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 8, fontSize: 13, color: 'var(--modal-fg)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.travar_nota_a4}
              onChange={(e) => setForm({ ...form, travar_nota_a4: e.target.checked })}
              style={{ width: 16, height: 16, padding: 0, margin: 0, flexShrink: 0 }}
            />
            Travar Nota em A4 — este cliente nunca sai como Nota Consumidor (cupom)
          </label>
          {/* Selo de consignacao (DA-371): so clientes com esta marca habilitam o modo
              "Remessa em consignacao" dentro do orcamento. */}
          <label style={{ gridColumn: 'span 6', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 8, fontSize: 13, color: 'var(--modal-fg)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.consignacao}
              onChange={(e) => setForm({ ...form, consignacao: e.target.checked })}
              style={{ width: 16, height: 16, padding: 0, margin: 0, flexShrink: 0 }}
            />
            Cliente de obra — leva material que pode devolver
          </label>
          {/* Selo do Programa do Prestador (DA-435). So aparece pro dono: entrar no programa e
              ADESAO (o markup e a retencao sao combinados com ele), nao cadastro de balcao. O
              backend recusa de verdade — aqui so decidimos se mostra. */}
          {ehDono && (
            <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 8, fontSize: 13, color: 'var(--modal-fg)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.programa_prestador}
                onChange={(e) => {
                  // DA-437: sem WhatsApp o parceiro nao tem como consultar o saldo, e o programa
                  // vive disso. DESMARCAR segue livre — tirar do programa nao depende de canal.
                  // Mesma medida do backend (10+ digitos), nao "tem linha na lista": um numero
                  // pela metade passaria aqui e tomaria erro seco la, que e' como front e back
                  // divergem calados.
                  const temWa = form.telefones.some((t) => (t.numero ?? '').replace(/\D/g, '').length >= 10);
                  if (e.target.checked && !temWa) {
                    void dialog.alerta(
                      'Este cliente nao tem WhatsApp cadastrado.\n\n'
                      + 'O parceiro consulta o saldo dele digitando "extrato" pro WhatsApp da loja — '
                      + 'sem numero, o credito nasce e ele nunca ve.\n\n'
                      + 'Cadastre o WhatsApp aqui embaixo e marque o selo depois.',
                      { titulo: 'Falta o WhatsApp' },
                    );
                    return; // o checkbox nao marca
                  }
                  setForm({ ...form, programa_prestador: e.target.checked });
                }}
                style={{ width: 16, height: 16, padding: 0, margin: 0, flexShrink: 0 }}
              />
              Parceiro FIODECOBRE — informa o valor ao cliente dele e recebe a diferenca em credito
            </label>
          )}
          <SecaoForm icone={Phone} titulo="Contato" />
          <label style={{ gridColumn: 'span 3' }}>
            Telefone
            <PhoneInput value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v })} />
          </label>
          <div style={{ gridColumn: 'span 5' }}>
            <label style={{ display: 'block', marginBottom: 4 }}>WhatsApp</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {form.telefones.map((t, idx) => {
                const wa = waInfo.get(chaveWA(t.numero));
                return (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => definirPrincipal(idx)}
                    title={t.principal ? 'Numero principal — usado por NF/orcamento/Pix' : 'Tornar este o numero principal'}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 4,
                      fontSize: 12, whiteSpace: 'nowrap', cursor: 'pointer', border: '1px solid var(--input-border)',
                      background: t.principal ? '#fde68a' : 'transparent',
                      color: t.principal ? '#000' : 'var(--text-muted)',
                      fontWeight: t.principal ? 700 : 400,
                    }}
                  >
                    <Star size={13} fill={t.principal ? '#000' : 'none'} /> {t.principal ? 'Principal' : 'Tornar principal'}
                  </button>
                  <Avatar nome={wa?.nome || formatTelefoneBR(t.numero)} fotoUrl={wa?.foto_url} tamanho={32} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {wa?.nome && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span
                          title={wa.janela_aberta ? 'Janela de 24h aberta — pode enviar mensagem livre' : 'Janela de 24h fechada — so template'}
                          style={{ flex: '0 0 auto', width: 8, height: 8, borderRadius: '50%', background: wa.janela_aberta ? '#3b82f6' : '#9ca3af' }}
                        />
                        {wa.nome}
                      </span>
                    )}
                    <span style={{ fontSize: 14 }}>{formatTelefoneBR(t.numero)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removerNumero(idx)}
                    title="Remover numero"
                    style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--input-border)', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                );
              })}
              <WhatsAppPickerInput
                value=""
                placeholder={form.telefones.length ? '+ Adicionar outro WhatsApp...' : 'Clique pra escolher contato...'}
                onChange={(v) => adicionarNumero(v)}
                onNomeContato={(nome) => setForm((f) => (f.nome?.trim() ? f : { ...f, nome }))}
              />
            </div>
          </div>
          <label style={{ gridColumn: 'span 4' }}>
            Email
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <SecaoForm icone={MapPin} titulo="Endereço" />
          <label style={{ gridColumn: 'span 3' }}>
            CEP
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <input
                value={form.cep}
                onChange={(e) => setForm({ ...form, cep: e.target.value })}
                placeholder="00000-000"
                style={{ flex: '0 1 50%', minWidth: 0 }}
              />
              <button
                type="button"
                // btn-primario = azul do tema (mesmo do "Buscar Informacoes"). Nao chumbar hex:
                // cinza fixo nao acompanhava o tema e ainda deixava a acao apagada na grade.
                className="btn-primario"
                onClick={buscarEndereco}
                disabled={buscandoCep}
                title="Preencher rua, bairro, cidade e UF pelo CEP"
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {buscandoCep ? 'Buscando...' : 'Buscar endereço'}
              </button>
            </div>
          </label>
          <label style={{ gridColumn: 'span 5' }}>
            Endereco
            <UpperInput
              value={form.endereco}
              onChange={(v) => setForm({ ...form, endereco: v })}
              readOnly={cepAtivo && !!form.endereco}
              style={cepAtivo && !!form.endereco ? travadoStyle : undefined}
              title={cepAtivo && !!form.endereco ? 'Preenchido pelo CEP — use "Remover CEP" pra editar a mao' : undefined}
            />
            {form.endereco.trim().length === 1 && (
              <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                Endereço incompleto — a nota exige 2+ caracteres. Falta o tipo da rua (ex: "RUA F"). Use "Buscar endereço" pelo CEP.
              </span>
            )}
          </label>
          <label style={{ gridColumn: 'span 1' }}>
            Numero
            <input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} />
          </label>
          <label style={{ gridColumn: 'span 3' }}>
            Complemento
            <UpperInput value={form.complemento} onChange={(v) => setForm({ ...form, complemento: v })} />
          </label>
          <label style={{ gridColumn: 'span 4' }}>
            Bairro
            <UpperInput
              value={form.bairro}
              onChange={(v) => setForm({ ...form, bairro: v })}
              readOnly={cepAtivo && !!form.bairro}
              style={cepAtivo && !!form.bairro ? travadoStyle : undefined}
            />
          </label>
          {/* DA-878: Cidade ESCOLHE do cadastro de Cidades (lista oficial do IBGE) — nome, UF e
              codigo IBGE vem juntos, e a UF deixa de ser digitada. Valor antigo fora do cadastro
              aparece como "(nao cadastrada)" pra nao abrir vazio e apagar o dado ao salvar. */}
          <div style={{ gridColumn: 'span 4' }}>
            <label style={{ display: 'block', marginBottom: 4 }}>Cidade</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ComboBox
                  value={form.codigo_ibge_cidade && cidades.some((c) => c.codigo_ibge === form.codigo_ibge_cidade)
                    ? form.codigo_ibge_cidade
                    : (form.cidade ? '__atual__' : null)}
                  onChange={(v) => {
                    if (v === '' || v == null) { setForm((f) => ({ ...f, cidade: '', uf: '', codigo_ibge_cidade: '' })); return; }
                    if (v === '__atual__') return;
                    const c = cidades.find((x) => x.codigo_ibge === String(v));
                    if (c) aplicarCidade(c);
                  }}
                  opcoes={[
                    ...(form.cidade && !cidades.some((c) => c.codigo_ibge === form.codigo_ibge_cidade)
                      ? [{ value: '__atual__', label: `${form.cidade}${form.uf ? ` / ${form.uf}` : ''} (nao cadastrada)` }]
                      : []),
                    ...cidades.map((c) => ({ value: c.codigo_ibge, label: `${c.nome} / ${c.uf}` })),
                  ]}
                  placeholder="Escolha a cidade..."
                  disabled={cepAtivo && !!form.cidade}
                />
              </div>
              <button
                type="button"
                className="btn-primario"
                onClick={() => setNovaCidade(true)}
                disabled={cepAtivo && !!form.cidade}
                title="Cadastrar uma cidade nova (lista oficial do IBGE)"
                style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 10px', flex: '0 0 auto' }}
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
          <label style={{ gridColumn: 'span 1' }}>
            UF
            <input value={form.uf} readOnly tabIndex={-1} title="A UF vem junto com a cidade" style={travadoStyle} />
          </label>
        </div>
      </Modal>

      {novaCidade && (
        <ModalNovaCidade
          jaCadastradas={cidades.map((c) => c.codigo_ibge)}
          onFechar={() => setNovaCidade(false)}
          onSalvo={(c) => { aplicarCidade(c); setNovaCidade(false); }}
        />
      )}

      <ConfirmModal aberto={!!confirmar} titulo="Inativar cliente" mensagem={`Inativar "${confirmar?.nome}"?`} textoConfirmar="Inativar" perigoso onConfirmar={remover} onCancelar={() => setConfirmar(null)} />

      <ConfirmModal
        aberto={confirmarRemoverCep}
        titulo="Remover CEP"
        mensagem="Isto limpa o CEP e o endereço preenchido e libera os campos pra digitar a mão. Tem certeza?"
        textoConfirmar="Remover CEP"
        onConfirmar={removerCep}
        onCancelar={() => setConfirmarRemoverCep(false)}
      />
    </div>
  );
}
